import React, { useMemo } from 'react';
import { BookOpen, CalendarClock, FileBadge2, Info } from 'lucide-react';
import { Box, Paper, Stack, Typography } from '@mui/material';

function formatSpanishDateTime(input) {
	if (!input) return '-';

	const date = new Date(input);
	if (Number.isNaN(date.getTime())) return '-';

	return date.toLocaleString('es-MX', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
}

function normalizeDifficulty(value) {
	const normalized = String(value || '').toLowerCase();

	if (normalized === 'hard') {
		return { label: 'Dificil', color: '#dc2626', dot: '#ef4444' };
	}

	if (normalized === 'easy') {
		return { label: 'Facil', color: '#15803d', dot: '#22c55e' };
	}

	return { label: 'Media', color: '#b45309', dot: '#f59e0b' };
}

function getQuestionTypeLabel(question) {
	if (question?.is_clinical_case_question) return 'Caso clinico';
	return 'Pregunta directa';
}

function DetailRow({ label, value, valueNode }) {
	return (
		<Box
			sx={{
				display: 'grid',
				gridTemplateColumns: { xs: '1fr', sm: '130px 1fr' },
				gap: { xs: 0.2, sm: 1.2 },
				py: 0.35,
			}}
		>
			<Typography sx={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>{label}</Typography>
			{valueNode || (
				<Typography sx={{ fontSize: 16, color: '#1e293b', fontWeight: 500, lineHeight: 1.35 }}>
					{value || '-'}
				</Typography>
			)}
		</Box>
	);
}

export default function PreguntaInfo({
	reviewPayload,
	questionPayload,
	currentQuestionId,
	currentQuestionPosition,
	title = 'Informacion de la pregunta',
}) {
	const navigator = useMemo(() => {
		return Array.isArray(reviewPayload?.navigator) ? reviewPayload.navigator : [];
	}, [reviewPayload]);
	const question = questionPayload?.question || {};
	const specialty = questionPayload?.specialty || {};
	const topic = questionPayload?.topic || {};
	const result = questionPayload?.result || {};

	const navigatorItem = useMemo(() => {
		if (currentQuestionId) {
			return navigator.find((item) => item?.question_id === currentQuestionId) || null;
		}

		const position = Number(currentQuestionPosition);
		if (Number.isFinite(position) && position > 0) {
			return navigator.find((item) => Number(item?.position) === position) || null;
		}

		return null;
	}, [navigator, currentQuestionId, currentQuestionPosition]);

	const difficulty = normalizeDifficulty(question?.difficulty);

	const materia =
		specialty?.name ||
		navigatorItem?.specialty_name ||
		'-';

	const tema =
		topic?.name ||
		navigatorItem?.topic_name ||
		'-';

	const questionType = getQuestionTypeLabel(question);

	const answeredAt =
		result?.answered_at ||
		question?.answered_at ||
		null;

	return (
		<Paper
			elevation={0}
			sx={{
				border: '1px solid #e2e8f0',
				borderRadius: '14px',
				p: { xs: 1.4, md: 1.8 },
				backgroundColor: '#ffffff',
			}}
		>
			<Stack direction="row" spacing={0.9} alignItems="center" sx={{ mb: 1.1 }}>
				<Box sx={{ width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<Info size={16} color="#334155" />
				</Box>
				<Typography sx={{ fontSize: 18, color: '#0f172a', fontWeight: 700, lineHeight: 1, display: 'flex', alignItems: 'center' }}>
					{title}
				</Typography>
			</Stack>

			<Stack spacing={0.35}>
				<DetailRow label="Materia" value={materia} />
				<DetailRow label="Tema" value={tema} />

				<DetailRow
					label="Dificultad"
					valueNode={
						<Stack direction="row" spacing={0.6} alignItems="center" sx={{ minHeight: 20 }}>
							<Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
								<Box
									sx={{
										width: 8,
										height: 8,
										borderRadius: '9999px',
										backgroundColor: difficulty.dot,
									}}
								/>
							</Box>
							<Typography sx={{ fontSize: 16, color: difficulty.color, fontWeight: 600, lineHeight: 1, display: 'flex', alignItems: 'center' }}>
								{difficulty.label}
							</Typography>
						</Stack>
					}
				/>

				<DetailRow
					label="Tipo"
					valueNode={
						<Stack direction="row" spacing={0.6} alignItems="center" sx={{ minHeight: 20 }}>
							<Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
								<FileBadge2 size={15} color="#64748b" />
							</Box>
							<Typography sx={{ fontSize: 16, color: '#1e293b', fontWeight: 500, lineHeight: 1, display: 'flex', alignItems: 'center' }}>
								{questionType}
							</Typography>
						</Stack>
					}
				/>

				<DetailRow
					label="Fecha respondida"
					valueNode={
						<Stack direction="row" spacing={0.6} alignItems="center" sx={{ minHeight: 20 }}>
							<Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
								<CalendarClock size={15} color="#64748b" />
							</Box>
							<Typography sx={{ fontSize: 16, color: '#1e293b', fontWeight: 500, lineHeight: 1, display: 'flex', alignItems: 'center' }}>
								{formatSpanishDateTime(answeredAt)}
							</Typography>
						</Stack>
					}
				/>
			</Stack>

			<Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 1.2, color: '#94a3b8' }}>
				<Box sx={{ width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<BookOpen size={13} />
				</Box>
				<Typography sx={{ fontSize: 11, lineHeight: 1, display: 'flex', alignItems: 'center' }}>
					Datos e informacion de la pregunta que estas visualizando.
				</Typography>
			</Stack>
		</Paper>
	);
}
